import { ProductFitment } from '../../app/models/Product';

export const FITMENT_CHANGE_LIMIT = 500;
export const fitmentKey = (row: ProductFitment) => JSON.stringify(Object.entries(row.fieldValues ?? {
    year: row.year, make: row.make, model: row.model, submodel: row.submodel,
}).filter(([, value]) => value?.trim()).map(([key, value]) => [key.toLowerCase(), value.trim().toLowerCase()]).sort(([a], [b]) => a.localeCompare(b)));
const signature = (row: ProductFitment) => JSON.stringify([fitmentKey(row), (row.notes ?? []).map(note => note.text.trim()).filter(Boolean)]);
export type PendingFitment = { before?: ProductFitment; after?: ProductFitment; replacementSources?: ProductFitment[] };

// The loaded page and the save draft have independent lifetimes.
export class FitmentDraft {
    constructor(readonly page: ProductFitment[] = [], readonly changes = new Map<string, PendingFitment>()) {}

    receive(page: ProductFitment[]) { return new FitmentDraft(page, this.changes); }
    discard() { return new FitmentDraft(this.page); }
    visible() {
        const pageKeys = new Set(this.page.map(fitmentKey));
        return [
            ...this.page.flatMap(row => {
                const edit = this.changes.get(fitmentKey(row));
                return edit ? edit.after ? [edit.after] : [] : [row];
            }),
            ...Array.from(this.changes).flatMap(([key, edit]) => !edit.before && edit.after && !pageKeys.has(key) ? [edit.after] : []),
        ];
    }
    stage(next: ProductFitment[]) {
        const previous = new Map(this.visible().map(row => [fitmentKey(row), row]));
        const desired = new Map(next.map(row => [fitmentKey(row), row]));
        const changes = new Map(this.changes);
        for (const key of Array.from(new Set([...Array.from(previous.keys()), ...Array.from(desired.keys())]))) {
            const current = previous.get(key);
            const after = desired.get(key);
            if (current && after && signature(current) === signature(after)) continue;
            const before = changes.has(key) ? changes.get(key)!.before : current;
            if ((!before && !after) || (before && after && signature(before) === signature(after))) changes.delete(key);
            else changes.set(key, { before, after, replacementSources: changes.get(key)?.replacementSources });
        }
        if (changes.size > FITMENT_CHANGE_LIMIT) throw new Error(`This action would leave ${changes.size.toLocaleString()} unsaved changes. Save or discard first. You can change up to ${FITMENT_CHANGE_LIMIT} fitments per save; larger actions must be split into smaller selections or made through Imports.`);
        return new FitmentDraft(this.page, changes);
    }
    // Preserve the original assignment identities through repeated edits and page changes.
    // Added years depend on the edited group; existing years use their matching source.
    replace(originals: ProductFitment[], replacements: ProductFitment[], next: ProductFitment[]) {
        const staged = this.stage(next);
        const changes = new Map(staged.changes);
        for (const replacement of replacements) {
            const key = fitmentKey(replacement);
            const pending = changes.get(key);
            if (!pending || pending.before) continue;
            const sameYear = originals.filter(row => (row.fieldValues?.year ?? row.year) === (replacement.fieldValues?.year ?? replacement.year));
            const sources = (sameYear.length ? sameYear : originals).flatMap(row => {
                const original = this.changes.get(fitmentKey(row));
                return original ? original.before ? [original.before] : original.replacementSources ?? [] : [row];
            });
            changes.set(key, { ...pending, replacementSources: Array.from(new Map(sources.map(row => [row.id, row])).values()) });
        }
        return new FitmentDraft(staged.page, changes);
    }
    undo(key: string | readonly string[]) {
        const changes = new Map(this.changes);
        for (const entry of typeof key === 'string' ? [key] : key) changes.delete(entry);
        return new FitmentDraft(this.page, changes);
    }
    payload(includeReplacementSources = false) {
        return Array.from(this.changes.values()).map(({ before, after, replacementSources }) => ({
            id: before?.id, version: before?.version, remove: !after,
            fieldValues: before ? {} : after?.fieldValues ?? { year: after!.year, make: after!.make, model: after!.model, submodel: after!.submodel },
            notes: after ? (after.notes ?? []).map(note => note.text).filter(Boolean) : undefined,
            ...(includeReplacementSources && !before && after && replacementSources?.length ? {
                replacementSources: replacementSources.map(row => ({ id: row.id, version: row.version })),
                preserveSourceNotes: replacementSources.every(row => JSON.stringify((row.notes ?? []).map(note => note.text.trim()).filter(Boolean))
                    === JSON.stringify((after.notes ?? []).map(note => note.text.trim()).filter(Boolean))),
            } : {}),
        }));
    }
}
